import { styled } from '@mui/material/styles';
import { Box, InputBase } from '@mui/material';
import ReactGA from "react-ga4";

import Icon from '../../components/Icon';
import TagPill from '../../components/TagPill';
import { ICONS } from '../../shared/icons.generated';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import type { VocabularyRef } from '../../shared/opportunity';
import {
  CORE_DAYS,
  OTHER_FILTERS,
  POST_WEEK,
  PRE_WEEK,
  TIME_SLOTS,
  type OtherFilter,
  type TimeSlot,
} from '../../shared/taxonomy';
import type { FilterState } from './filters';

/**
 * The filter sidebar.
 *
 * The options carry no counts. The list is paged from the API rather than held
 * in memory, so there is nothing in the browser to count each option against,
 * and an option is never dimmed for the same reason. The two toggles still say
 * how many events they would add, from the count endpoint.
 */

const Section = styled('section')({
  marginBottom: 30,
  // The day, time and intent boxes are sized against the section's own width.
  containerType: 'inline-size',
});

const SectionHead = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 12,
});

const SectionTitle = styled('h2')({
  margin: 0,
  fontFamily: FONTS.display,
  // Cy Text Bold, per the brand guidelines for section headers.
  fontWeight: 700,
  fontSize: 21,
  letterSpacing: '-0.005em',
  color: BRAND.parchment,
});

const ResetLink = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 12,
  fontWeight: 700,
  color: 'rgba(243,238,231,0.6)',
  '&:hover': { color: BRAND.parchment },
  '&:disabled': { opacity: 0.3, cursor: 'default' },
});

const SearchWrap = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  border: `1px solid rgba(243,238,231,0.5)`,
  borderRadius: 999,
  padding: '9px 16px',
  marginBottom: 18,
  transition: 'border-color 120ms ease',
  '&:focus-within': { borderColor: BRAND.parchment },
});

const Count = styled('span')({
  fontFamily: FONTS.body,
  fontSize: 10,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  opacity: 0.62,
  marginLeft: 'auto',
  paddingLeft: 6,
});

/**
 * A section's Reset, sized to sit on the heading's baseline. With nothing to
 * reset it is the reference's quiet grey rather than faded out; with something
 * to reset it is full parchment.
 */
const SectionReset = styled(ResetLink)({
  fontSize: 16,
  color: BRAND.parchment,
  transition: 'color 160ms ease',
  '&:hover': { color: BRAND.parchment, opacity: 0.8 },
  '&:disabled': { opacity: 1, color: 'rgba(243,238,231,0.6)' },
});

/**
 * The day, time-of-day and intent filters, after the filter reference.
 *
 * Its measurements are for a 460px-wide section, the desktop sidebar's natural
 * width. `cq` turns one of them into a share of the section's width, so the
 * same proportions hold in the tablet drawer and on a phone, and `fluid` does
 * the same for a font size without letting it drop below a readable floor.
 */
const cq = (px: number) => `${Number((px / 4.6).toFixed(3))}cqw`;
const fluid = (px: number, min: number) => `max(${min}px, ${cq(px)})`;

/** The labels outside the boxes: weekdays, hours and intents. */
const QUIET = 'rgba(243,238,231,0.85)';

/**
 * The icon artwork is drawn at one scale for every icon of a kind rather than
 * fitted to a square, so a wide handshake stays wider than a tall glass. `per`
 * is px of icon per unit of viewBox width, at the reference width.
 */
function iconWidth(name: string, per: number): string {
  const viewBox = ICONS[name]?.viewBox.split(' ').map(Number);
  return cq((viewBox?.[2] ?? 0) * per);
}

/** An intent label on two balanced lines, the longer first when they tie. */
function twoLines(label: string): [string, string] | [string] {
  const words = label.split(' ');
  if (words.length < 2) return [label];
  let best: [string, string] = [label, ''];
  let longest = Infinity;
  for (let i = 1; i < words.length; i++) {
    const first = words.slice(0, i).join(' ');
    const second = words.slice(i).join(' ');
    const length = Math.max(first.length, second.length);
    if (length <= longest) {
      best = [first, second];
      longest = length;
    }
  }
  return best;
}

/**
 * One choice: the box, plus the words that sit outside it, all one button so
 * the words are as clickable as the box. Hovering lifts an unselected box's
 * fill from 6% to 20%.
 */
const Choice = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  minWidth: 0,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  font: 'inherit',
  color: BRAND.parchment,
  '@media (hover: hover)': {
    '&:hover > [data-box="off"]': { backgroundColor: 'rgba(243,238,231,0.2)' },
  },
  '&:focus-visible': { outline: 'none' },
  '&:focus-visible > [data-box]': { outline: `2px solid ${BRAND.parchment}`, outlineOffset: 2 },
});

/** Unselected: a thin parchment outline around a 6% parchment fill. Selected: solid parchment. */
const ChoiceBox = styled('span', { shouldForwardProp: (p) => p !== '$on' })<{ $on: boolean }>(({ $on }) => ({
  position: 'relative',
  display: 'grid',
  placeItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
  border: `1px solid ${$on ? BRAND.parchment : 'rgba(243,238,231,0.75)'}`,
  backgroundColor: $on ? BRAND.parchment : 'rgba(243,238,231,0.06)',
  color: $on ? BRAND.mocha : BRAND.parchment,
  transition: 'background-color 220ms ease, color 220ms ease, border-color 220ms ease',
}));

const DayGrid = styled('div')({
  marginTop: 9,
  display: 'grid',
  gridTemplateColumns: 'repeat(10, minmax(0, 1fr))',
  columnGap: cq(6.8),
});

const Weekday = styled('span')({
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: fluid(12, 11),
  lineHeight: 1.2,
  color: QUIET,
  whiteSpace: 'nowrap',
  marginBottom: cq(5),
});

const DayBox = styled(ChoiceBox)({
  aspectRatio: '122 / 162',
  borderRadius: cq(7.8),
});

const DayNumber = styled('span')({
  fontFamily: FONTS.condensed,
  fontWeight: 700,
  fontSize: fluid(24, 20),
  lineHeight: 1,
});

const WeekEnd = styled('span')({
  fontFamily: FONTS.serif,
  fontWeight: 500,
  fontSize: fluid(11.5, 10),
  lineHeight: 1.05,
  textAlign: 'center',
});

const SlotGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  columnGap: cq(30.6),
  marginTop: cq(16.3),
});

const SlotBox = styled(ChoiceBox)({
  display: 'block',
  aspectRatio: '281 / 258',
  borderRadius: cq(9.8),
});

/** Centred at these heights in the box, whatever the artwork's proportions. */
const SlotIcon = styled('span')({
  position: 'absolute',
  left: '50%',
  top: '39%',
  display: 'block',
  transform: 'translate(-50%, -50%)',
  '& > svg': { width: '100%', height: 'auto' },
});

const SlotName = styled('span')({
  position: 'absolute',
  left: '50%',
  top: '80%',
  transform: 'translate(-50%, -50%)',
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: fluid(12.5, 11),
  lineHeight: 1,
  whiteSpace: 'nowrap',
});

const SlotHint = styled('span')({
  marginTop: cq(6),
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: fluid(11, 10),
  lineHeight: 1.3,
  color: QUIET,
  whiteSpace: 'nowrap',
});

/** One row of six once the section is about the reference's width; two rows of three below that. */
const IntentGrid = styled('div')({
  marginTop: 5,
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  columnGap: cq(10.1),
  rowGap: 10,
  '& [data-box]': { height: cq(92) },
  '@container (min-width: 440px)': {
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    '& [data-box]': { height: 'auto', aspectRatio: '209 / 226' },
  },
});

const IntentBox = styled(ChoiceBox)({
  borderRadius: cq(7.8),
  '& > span': { display: 'block' },
  '& > span > svg': { width: '100%', height: 'auto' },
});

/** Always two lines tall, so one-line labels sit centred beside two-line ones. */
const IntentLabel = styled('span')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '2.3em',
  marginTop: cq(4),
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: fluid(11, 10.5),
  lineHeight: 1.15,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  color: QUIET,
});

/** A bare button so TagPill can own the entire appearance. */
const TagButton = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  width: '100%',
  display: 'block',
  textAlign: 'left',
  cursor: 'pointer',
});

const OtherButton = styled('button')<{ $on: boolean }>(({ $on }) => ({
  appearance: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: 999,
  fontFamily: FONTS.body,
  fontSize: 12.5,
  fontWeight: 600,
  background: $on ? BRAND.parchment : 'transparent',
  color: $on ? BRAND.mocha : BRAND.parchment,
  border: `1px solid ${$on ? BRAND.parchment : 'rgba(243,238,231,0.4)'}`,
  transition: 'background 120ms ease, color 120ms ease',
  '&:hover': { borderColor: BRAND.parchment },
}));

/** A labelled on/off switch for the two toggles that widen what Explore lists. */
const ToggleRow = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  width: '100%',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '7px 0',
  textAlign: 'left',
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: 600,
  color: BRAND.parchment,
  '&:focus-visible': { outline: `2px solid ${BRAND.parchment}`, outlineOffset: 2, borderRadius: 6 },
});

const Switch = styled('span', { shouldForwardProp: (p) => p !== '$on' })<{ $on: boolean }>(({ $on }) => ({
  position: 'relative',
  flexShrink: 0,
  width: 34,
  height: 20,
  borderRadius: 999,
  background: $on ? BRAND.parchment : 'transparent',
  border: `1px solid ${$on ? BRAND.parchment : 'rgba(243,238,231,0.45)'}`,
  transition: 'background 140ms ease, border-color 140ms ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 2,
    left: $on ? 16 : 2,
    width: 14,
    height: 14,
    borderRadius: '50%',
    background: $on ? BRAND.mocha : 'rgba(243,238,231,0.7)',
    transition: 'left 160ms ease, background 140ms ease',
  },
}));

interface SidebarProps {
  filters: FilterState;
  /** From `GET /taxonomy`, in its order. */
  topics: VocabularyRef[];
  goals: VocabularyRef[];
  onQuery: (q: string) => void;
  onToggleDay: (key: string) => void;
  onToggleSlot: (slot: TimeSlot) => void;
  onToggleGoal: (key: string) => void;
  onToggleTopic: (key: string) => void;
  onToggleOther: (id: OtherFilter) => void;
  onResetFacet: (facet: 'days' | 'slots' | 'goals' | 'topics' | 'others') => void;
  onResetAll: () => void;
  /** How many events the toggle would add back; null until the first count arrives. */
  hiddenCount: number | null;
  onToggleShowHidden: () => void;
}

export default function Sidebar({
  filters,
  topics,
  goals,
  onQuery,
  onToggleDay,
  onToggleSlot,
  onToggleGoal,
  onToggleTopic,
  onToggleOther,
  onResetFacet,
  onResetAll,
  hiddenCount,
  onToggleShowHidden,
}: SidebarProps) {
  const dayButtons = [
    { key: PRE_WEEK, label: 'Pre', sub: 'week', weekday: '' },
    ...CORE_DAYS.map((d) => ({ key: d.key, label: d.label, sub: '', weekday: d.sublabel })),
    { key: POST_WEEK, label: 'Post', sub: 'week', weekday: '' },
  ];

  return (
    <Box sx={{ pb: 8 }}>
      <SearchWrap>
        <SearchGlyph />
        <InputBase
          value={filters.query}
          onChange={(e) => onQuery(e.target.value)}
          onBlur={(e) => {
            if (e.target.value !== '') {
              // Log event when search box loses focus to hopefully catch what a user searched for
              ReactGA.event('event_search', { search_term: e.target.value });
            }
          }}
          placeholder="Search for events..."
          inputProps={{ 'aria-label': 'Search events' }}
          sx={{
            flex: 1,
            fontFamily: FONTS.body,
            fontSize: 14,
            color: BRAND.parchment,
            '& input::placeholder': { color: 'rgba(243,238,231,0.5)', opacity: 1 },
          }}
        />
        {filters.query && (
          <ResetLink onClick={() => onQuery('')} aria-label="Clear search">
            ✕
          </ResetLink>
        )}
      </SearchWrap>

      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <ResetLink onClick={onResetAll} style={{ fontSize: 13, color: BRAND.parchment }}>
          Reset all filters
        </ResetLink>
      </Box>

      <Box sx={{ mb: 3.5 }}>
        <ToggleRow type="button" role="switch" aria-checked={filters.showHidden} onClick={onToggleShowHidden}>
          <Switch $on={filters.showHidden} aria-hidden="true" />
          <span>Show events marked “Not Interested”</span>
          <Count>{hiddenCount ?? ''}</Count>
        </ToggleRow>
      </Box>

      {/* ---------------- days ---------------- */}
      <Section style={{ marginBottom: 33 }}>
        <SectionHead>
          <SectionTitle>Filter by day(s)</SectionTitle>
          <SectionReset onClick={() => onResetFacet('days')} disabled={filters.days.size === 0}>
            Reset
          </SectionReset>
        </SectionHead>

        <DayGrid>
          {dayButtons.map((d) => {
            const on = filters.days.has(d.key);
            return (
              <Choice
                key={d.key}
                type="button"
                aria-pressed={on}
                aria-label={[d.weekday, d.label, d.sub].filter(Boolean).join(' ')}
                onClick={() => onToggleDay(d.key)}
              >
                <Weekday aria-hidden="true">{d.weekday || '\u00a0'}</Weekday>
                <DayBox data-box={on ? 'on' : 'off'} $on={on}>
                  {d.sub ? (
                    <WeekEnd>
                      {d.label}
                      <br />
                      {d.sub}
                    </WeekEnd>
                  ) : (
                    <DayNumber>{d.label}</DayNumber>
                  )}
                </DayBox>
              </Choice>
            );
          })}
        </DayGrid>

        <SlotGrid>
          {TIME_SLOTS.map((slot) => {
            const on = filters.slots.has(slot.id);
            const name = `time:${slot.id}`;
            return (
              <Choice
                key={slot.id}
                type="button"
                aria-pressed={on}
                aria-label={`${slot.label}, ${slot.hint}`}
                onClick={() => onToggleSlot(slot.id)}
              >
                <SlotBox data-box={on ? 'on' : 'off'} $on={on}>
                  {/* One scale for all four: a sunrise is wider than a moon. */}
                  <SlotIcon style={{ width: iconWidth(name, 0.059) }}>
                    <Icon name={name} size="100%" />
                  </SlotIcon>
                  <SlotName>{slot.label}</SlotName>
                </SlotBox>
                <SlotHint aria-hidden="true">({slot.hint})</SlotHint>
              </Choice>
            );
          })}
        </SlotGrid>
      </Section>

      {/* ---------------- I want to ---------------- */}
      <Section>
        <SectionHead>
          <SectionTitle>I want to...</SectionTitle>
          <SectionReset onClick={() => onResetFacet('goals')} disabled={filters.goals.size === 0}>
            Reset
          </SectionReset>
        </SectionHead>
        <IntentGrid>
          {goals.map((goal) => {
            const on = filters.goals.has(goal.key);
            const name = `goal:${goal.key}`;
            const [first, second] = twoLines(goal.label);
            return (
              <Choice
                key={goal.key}
                type="button"
                aria-pressed={on}
                aria-label={goal.label}
                onClick={() => onToggleGoal(goal.key)}
              >
                <IntentBox data-box={on ? 'on' : 'off'} $on={on}>
                  <span style={{ width: iconWidth(name, 0.738) }}>
                    <Icon name={name} size="100%" />
                  </span>
                </IntentBox>
                <IntentLabel aria-hidden="true">
                  <span>
                    {first}
                    {second && (
                      <>
                        <br />
                        {second}
                      </>
                    )}
                  </span>
                </IntentLabel>
              </Choice>
            );
          })}
        </IntentGrid>
      </Section>

      {/* ---------------- topics ---------------- */}
      <Section>
        <SectionHead>
          <SectionTitle>I'm interested in...</SectionTitle>
          <SectionReset onClick={() => onResetFacet('topics')} disabled={filters.topics.size === 0}>
            Reset
          </SectionReset>
        </SectionHead>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.8 }}>
          {topics.map((topic) => {
            const on = filters.topics.has(topic.key);
            return (
              <TagButton
                key={topic.key}
                aria-pressed={on}
                aria-label={topic.label}
                onClick={() => onToggleTopic(topic.key)}
              >
                <TagPill topic={topic} on={on} />
              </TagButton>
            );
          })}
        </Box>
      </Section>

      {/* ---------------- other ---------------- */}
      <Section>
        <SectionHead>
          <SectionTitle>Other filters</SectionTitle>
          <SectionReset onClick={() => onResetFacet('others')} disabled={filters.others.size === 0}>
            Reset
          </SectionReset>
        </SectionHead>
        {/* Two unrelated ideas — where it happens, and who can get in — so they
            are separated rather than run together as one row of pills. */}
        {[
          OTHER_FILTERS.filter((f) => f.id === 'inPerson' || f.id === 'online'),
          OTHER_FILTERS.filter((f) => f.id === 'freePublic' || f.id === 'hideInviteOnly'),
        ].map((group, i) => (
          <Box
            key={group.map((g) => g.id).join()}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.9, mt: i === 0 ? 0 : 2.2 }}
          >
            {group.map((f) => {
              const on = filters.others.has(f.id);
              return (
                <OtherButton
                  key={f.id}
                  $on={on}
                  aria-pressed={on}
                  onClick={() => onToggleOther(f.id)}
                >
                  {f.label}
                </OtherButton>
              );
            })}
          </Box>
        ))}
      </Section>
    </Box>
  );
}

function SearchGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        d="M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm5.5 12.5L21 21"
      />
    </svg>
  );
}
